import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import useGridRowSelection from "hooks/useGridRowSelection";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowGroupingModel,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import NameCell from "./Cells/NameCell";
import StatusCell from "./Cells/StatusCell";
import tagsService from "api/tagsService";
import statusService from "api/statusService";
import TagCell from "./Cells/TagCell";
import { plGridLocales } from "utils/muiGridLocales";
import ActionsContext from "contexts/ActionsContext";
import { IFILTERTASK } from "components/Tasks";
import { Types } from "components/TasksList/typings";
import { IFeed } from "dto/IFeed";
import DateCell from "./Cells/DateCell";
import CreatedByCell from "./Cells/CreatedByCell";
import CreatedForCell from "./Cells/CreatedForCell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import CustomToolbar from "components/CaseDossier/Toolbar";
import Spinner from "components/Spinner";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { IGridFilter } from "dto/IGridFilter";
import { ListType } from "dto/ListType";
import { StatusType } from "dto/IStatus";
import { TagType } from "dto/ITag";
import NotFoundScreen from "components/NotFoundScreen";
import ModalsManagerContext from "contexts/ModalsManager";
import { useTranslation } from "react-i18next";

const TasksListNew: FC<Props> = ({
  search,
  isDesk,
  setTask,
  showToolbar,
  hideColumnHeader,
}) => {
  const { t } = useTranslation();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
  const { modals, setModals } = useContext(ModalsManagerContext);

  const { caseId } = useParams();
  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData } = statusService.useGetStatuses();

  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const getStatus = (item: IFeed) => {
    const s = statuses.find((x) => x.Id == item.StatusId);

    if (!item.StatusId || !s) {
      const todoStatus = statuses.find((s) => s.GlobalType === 23);

      if (todoStatus) {
        return todoStatus.Name;
      }

      return null;
    }

    if (s) {
      return s.Name;
    }

    return null;
  };

  const getFirstTag = (item: IFeed) => {
    if (!item.TagIds || item.TagIds.length === 0) return null;

    const t = tags.find((x) => x.Id == item.TagIds[0]);

    if (t) {
      return t.Name;
    }

    return null;
  };

  const [filters] = useState<IFILTERTASK>({
    status: null,
    tag: null,
    assignedBy: null,
    assignedTo: null,
    startDate: null,
    endDate: null,
  });
  const [type, setType] = useState<Types>(Types.NOT_READY);

  const [filterStatusId, setFilterStatusId] = useState<number>(null);
  const [filterTagId, setFilterTagId] = useState<string>(null);
  const [filterUserId, setFilterUserId] = useState<string>(null);
  const [editFilter, setEditFilter] = useState<IGridFilter>(null);

  const [showTypeModal, setShowTypeModal] = useState(false);
  const handleChangeType = (newType: Types) => {
    setType(newType);
    setShowTypeModal(false);
  };

  const {
    data: tasksData,
    fetchNextPage,
    isLoading,
    refetch,
    hasNextPage,
  } = useInfiniteQuery(
    [
      "tasks",
      filters.assignedTo,
      filters.assignedBy,
      filters.tag,
      filters.status,
      filters.startDate,
      filters.endDate,
      type,
      search,
      filterStatusId,
      filterTagId,
      filterUserId,
    ],
    async ({ pageParam = 0 }) => {
      const query = new URLSearchParams(window.location.search);
      query.append("Page", pageParam);
      if (caseId) {
        query.append("CaseId", caseId!);
      }

      if (filters.assignedTo) {
        query.append("AssignedUserId", filters.assignedTo.Id);
      }
      if (filters.assignedBy) {
        query.append("AssignedByUserId", filters.assignedBy.Id);
      }
      if (filters.tag) {
        query.append("TagId", filters.tag.Id);
      }
      if (filters.status) {
        query.append("StatusId", String(filters.status.Id));
      }

      if (filterTagId) {
        query.append("TagId", filterTagId);
      }
      if (filterStatusId) {
        query.append("StatusId", String(filterStatusId));
      }

      if (filters.startDate) {
        query.append("start", filters.startDate.toISOString());
      }

      if (filters.startDate) {
        query.append("end", filters.endDate.toISOString());
      }

      if (search?.text) {
        query.append("Text", search.text);
      }

      if (isDesk) {
        console.log("is Desk");
        query.append("Desk", "true");
      }

      if (type !== Types.ALL) {
        query.append("Completed", type === Types.READY ? "true" : "false");
      }

      const res = await axiosInstance.get(`/task/listExtended?${query}`);

      return {
        items: res.data,
        LastPage: res.data.LastPage,
        nextPage: pageParam + 1,
      };
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.nextPage;
        }
        return undefined;
      },
    },
  );

  const tasksMapped = useMemo(
    () =>
      tasksData?.pages
        .map((p) => p.items.Items.filter((o: any) => o.Type === 1))
        .flat() ?? [],
    [tasksData],
  );

  const { tasks, setTasks } = useContext(ActionsContext);

  useEffect(() => {
    setTasks(tasksMapped);
    setIsInitialized(true);
  }, [tasksMapped]);


  useEffect(() => {
    setTasks([]);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      refetch();
    }
  }, [caseId]);

  const [isInitialized, setIsInitialized] = useState(false);

  const columns: GridColDef[] = [
    {
      field: "Label",
      minWidth: 280,
      flex: 1,
      headerName: t("tasks.subject"),
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.Title,
      groupingValueGetter: (value, row) =>
        row.Title ?? t("tasks.noSubject"),
      renderCell: (params: GridRenderCellParams<IFeed, string>) =>
        params.rowNode.type !== "group" ? (
          <NameCell element={params.row}></NameCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "CreatedBy",
      minWidth: 140,
      headerName: t("tasks.assignedBy"),
      renderHeader: () => {
        return <div className={styles.header}>{t("tasks.assignedByShort")}</div>;
      },
      headerAlign: "center",
      align: "center",
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.CreatedByUserLabel,
      groupingValueGetter: (value, row) =>
        row.CreatedByUserLabel ?? t("tasks.noAssignee"),
      renderCell: (params: GridRenderCellParams<IFeed, string>) =>
        params.rowNode.type !== "group" ? (
          <CreatedByCell element={params.row}></CreatedByCell>
        ) : (
          <div>{params.value}</div>
        ),
    },

    {
      field: "CreatedFor",
      minWidth: 140,
      headerName: t("tasks.executorOfTask"),
      renderHeader: () => {
        return <div className={styles.header}>{t("tasks.executor")}</div>;
      },
      headerAlign: "center",
      align: "center",
      valueGetter: (value, row) => row?.UserLabel,
      groupingValueGetter: (value, row) => row.UserLabel ?? t("tasks.noExecutor"),
      cellClassName: `${styles.nameCell}`,
      renderCell: (params: GridRenderCellParams<IFeed, string>) =>
        params.rowNode.type !== "group" ? (
          <CreatedForCell element={params.row}></CreatedForCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "Status",
      minWidth: 140,
      headerName: t("common.status"),
      headerAlign: "center",
      align: "center",
      cellClassName: `${styles.statusCell}`,
      groupingValueGetter: (value, row) => getStatus(row) ?? t("registry.noStatus"),
      renderCell: (params: GridRenderCellParams<IFeed, string>) =>
        params.rowNode.type !== "group" ? (
          <StatusCell element={params.row} statuses={statuses}></StatusCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "TagIds",
      minWidth: 140,
      headerName: t("forms.label"),
      headerAlign: "center",
      align: "center",
      cellClassName: `${styles.nameCell}`,
      groupingValueGetter: (value, row) => getFirstTag(row) ?? t("registry.noLabel"),
      renderCell: (params: GridRenderCellParams<IFeed, string>) =>
        params.rowNode.type !== "group" ? (
          <TagCell element={params.row} tags={tags}></TagCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "CreationDate",
      width: 140,
      headerName: t("columns.creationDate"),
      renderHeader: () => {
        return <div className={styles.header}>{t("tasks.createdAt")}</div>;
      },
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.FromDate,
      groupable: false,

      renderCell: (params: GridRenderCellParams<IFeed, string>) =>
        params.rowNode.type !== "group" ? (
          <DateCell element={params.row}></DateCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "ToDate",
      width: 140,
      groupable: true,
      headerName: t("tasks.dueTo"),
      renderHeader: () => {
        return <div className={styles.header}>{t("tasks.dueTo")}</div>;
      },
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) =>
        row.ToDate ? moment(row.ToDate).format("DD.MM.YY HH:mm") : null,
      groupingValueGetter: (value, row) =>
        row.ToDate ? moment(row.ToDate).format("DD.MM.YY") : t("common.noDate"),
    },
    {
      field: "ClosedDate",
      groupable: false,
      width: 140,
      headerName: t("tasks.completionDate"),
      renderHeader: () => {
        return <div className={styles.header}>{t("tasks.completionDate")}</div>;
      },
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) =>
        row.ClosedDate ? moment(row.ClosedDate).format("DD.MM.YY HH:mm") : null,
    },

    // {
    //   field: "menu",
    //   width: 40,
    //   headerName: "Menu",
    //   disableReorder: true,
    //   sortable: false,
    //   resizable: false,
    //   hideable: false,
    //   renderHeader: () => <div></div>,
    //   renderCell: (params: GridRenderCellParams<IFeed, string>) => (
    //     <MenuCell
    //       element={params.row}
    //       onDownload={onDownload}
    //       keys={keys}
    //     ></MenuCell>
    //   ),
    // },
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>([]);

  const CustomToolbar2 = () => {

    return (
      <CustomToolbar
        listName="taskList"
        editFilter={editFilter}
        setEditFilter={setEditFilter}
        listType={ListType.Tasks}
        statusId={filterStatusId}
        setStatusId={setFilterStatusId}
        tagId={filterTagId}
        setTagId={setFilterTagId}
        statusType={StatusType.TASKS}
        tagType={TagType.Tasks}
        userId={filterUserId}
        setUserId={setFilterUserId}
        dateFilterLabel={t("columns.date")}
        rightChildren={
          <div className={styles.type}>
            <div
              onClick={() => setShowTypeModal(!showTypeModal)}
              className={styles.typeText}
            >
              {type === Types.ALL
                ? t("common.all")
                : type === Types.READY
                ? t("actions.doneShort")
                : t("tasks.toDo")}
              <FontAwesomeIcon
                icon={showTypeModal ? faChevronUp : faChevronDown}
              />
            </div>
            {showTypeModal && (
              <div className={styles.dropdown}>
                <div
                  onClick={() => handleChangeType(Types.ALL)}
                  className={styles.box}
                >
                  {t("common.all")}
                </div>
                <div
                  onClick={() => handleChangeType(Types.READY)}
                  className={styles.box}
                >
                  {t("actions.doneShort")}
                </div>
                <div
                  onClick={() => handleChangeType(Types.NOT_READY)}
                  className={styles.box}
                >
                  {t("tasks.toDo")}
                </div>
              </div>
            )}
          </div>
        }
      ></CustomToolbar>
    );
  };

  const toolbar = () => <CustomToolbar2></CustomToolbar2>;

  const apiRef = useGridApiRef();

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem("grid_tasks_v2", JSON.stringify(currentState));
    }
  }, [apiRef]);

  const [initialState, setInitialState] = useState<GridInitialState>(null);
  const initialStateHidden = useKeepGroupedColumnsHidden({
    apiRef,
    initialState,
  });

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem("grid_tasks_v2");
    const data: GridInitialStatePremium = stateFromLocalStorage
      ? JSON.parse(stateFromLocalStorage)
      : ({
          sorting: { sortModel: [{ field: "CreationDate", sort: "desc" }] },
        } as GridInitialStatePremium);
    setInitialState(data);

    initialStateHidden.columns = data.columns;
    initialStateHidden.rowGrouping = data.rowGrouping;
    initialStateHidden.sorting = data.sorting;
    setColumnVisibilityModel(data?.columns?.columnVisibilityModel ?? {});
    setGroupingModel(data.rowGrouping?.model ?? []);

    // handle refresh and navigating away/refreshing
    window.addEventListener("beforeunload", saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener("beforeunload", saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const hiddenFields = [
    "Label",
    "IsFolder",
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    "Icons",
    "Menu",
  ];

  const columnsMemo = useMemo(() => columns, []);

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };

  if (
    !initialState ||
    !statusesData?.data ||
    !tagsData?.data ||
    !initialStateHidden
  ) {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        {tasks.length === 0 && !isLoading && isInitialized && (
          <div className={styles.empty}>
            <NotFoundScreen
              title={isDesk ? t("emptyStates.emptyDesk") : t("emptyStates.notFound")}
              text={
                isDesk
                  ? t("emptyStates.deskDescription")
                  : t("emptyStates.noResultsSuffix")
              }
              buttonName={isDesk ? `+ ${t("tasks.task")}` : null}
              onClick={() => setModals({ ...modals, createTask: true })}
            />
          </div>
        )}

        {(tasks?.length > 0 || isLoading || !isInitialized) && (
          <DataGridPremium
            apiRef={apiRef}
            sx={{
              border: 0,
              "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none !important",
                },
            }}
            initialState={initialStateHidden}
            localeText={plGridLocales}
            slots={{
              toolbar: toolbar,
              columnHeaders: hideColumnHeader ? () => null : undefined,
            }}
            showToolbar={showToolbar}
            slotProps={{
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
              columnsManagement: {
                getTogglableColumns,
              },
            }}
            loading={!isInitialized || isLoading}
            columnHeaderHeight={30}
            onRowsScrollEnd={() => {
              if (hasNextPage) {
                fetchNextPage();
              }
            }}
            disableRowSelectionOnClick
            rowSelectionModel={selectedRowIds}
            onRowClick={(a, event) => {
              handleRowClick(a, event);
              const issue = a.row as IFeed;
              setTask(issue);
            }}
            columns={columnsMemo}
            hideFooter={true}
            disablePivoting={true}
            rows={(() => { const _rows = tasks.map((c) => { (c as any).id = c.Id; return c; }); rowsRef.current = _rows; return _rows; })()}
            disableColumnFilter={true}
            disableAggregation={true}
            onColumnVisibilityModelChange={(m) => {
              console.log("ON Column visibility model changed", m);
              setColumnVisibilityModel(m);
              setTimeout(() => saveSnapshot(), 1000);
            }}
            onRowGroupingModelChange={(m) => {
              const newVisibility = { ...columnVisibilityModel };

              for (let i = 0; i < groupingModel.length; i++) {
                newVisibility[groupingModel[i]] = true;
              }

              for (let i = 0; i < m.length; i++) {
                newVisibility[m[i]] = false;
              }
              setColumnVisibilityModel(newVisibility);
              setGroupingModel(m);
            }}
            columnVisibilityModel={columnVisibilityModel}
          />
        )}
      </div>
    </div>
  );
};

export default TasksListNew;
